import { getCountry } from '@stax-core/lateralus-toolkit';
import {
  Brand,
  Merchant,
  Registration,
  RegistrationFieldPermissionsField,
  RegistrationRepresentative as Representative,
} from '@stax-core/types-omni';
import { Mutator } from 'final-form';
import { getCombinedFieldsVisibilityLevels } from '../../hooks';
import {
  formatFirstAndLastNameToFullName,
  formatStringToSentence,
} from '../../util/format.util';
import { get, isEmpty, set } from '../../util/object.util';
import {
  isAddressApo,
  isAddressPoBox,
  isMeaningfulString,
  isValidEmail,
  isValidPhoneNumber,
  isValidSSN,
} from '../../util/validator.util';
import {
  FinancialFieldsErrors,
  FinancialFieldsValues,
  RepresentativeErrors,
} from './FinancialFields.types';

export type PrimaryBankAccountFields = Pick<
  Registration,
  'bank_account_number' | 'bank_routing_number'
>;

export type SecondaryBankAccountFields = Pick<
  Registration,
  'secondary_bank_account_number' | 'secondary_bank_routing_number'
>;

export function hasPrimaryManualBankAccount(fields: PrimaryBankAccountFields) {
  return !!(fields.bank_account_number && fields.bank_routing_number);
}

export function hasSecondaryManualBankAccount(
  fields: SecondaryBankAccountFields,
) {
  return !!(
    fields.secondary_bank_account_number && fields.secondary_bank_routing_number
  );
}

export function toNumber(value: string): number {
  return isNaN(+value) ? 0 : +value;
}

export function mapFormValuesToPayload(
  formValues: FinancialFieldsValues,
): Partial<Registration> {
  const payload: Partial<Registration> = {
    title: formValues.title,
    user_dob: formValues.user_dob,
    user_ssn: formValues.user_ssn,
    first_name: formValues.first_name,
    last_name: formValues.last_name,
    email: formValues.email,
    phone_number: formValues.phone_number,
    owner_address_1: formValues.owner_address_1,
    owner_address_2: formValues.owner_address_2 || '',
    owner_address_city: formValues.owner_address_city,
    owner_address_state: formValues.owner_address_state,
    owner_address_country: formValues.owner_address_country,
    owner_address_zip: formValues.owner_address_zip,
    drivers_license_id: formValues.drivers_license_id,
    drivers_license_state: formValues.drivers_license_state,
    job_title: formValues.title,
    principal_owners_name: `${formValues.first_name} ${formValues.last_name}`,
    bank_account_owner_name: formValues.bank_account_owner_name,
    bank_account_type: formValues.bank_account_type,
    bank_routing_number: formValues.bank_routing_number,
    bank_account_number: formValues.bank_account_number,
    meta: {
      ...formValues.meta,
      ownership_percentage: formValues.meta?.ownership_percentage || '100',
      representatives: formValues.meta?.representatives,
    },
  };

  if (formValues.hasSecondaryAccount || formValues.addAccountManually) {
    // if the 'hasSecondaryAccount' checkbox is checked
    // then include the secondary bank account information in the payload
    payload.secondary_bank_account_owner_name =
      formValues.secondary_bank_account_owner_name;
    payload.secondary_bank_account_type =
      formValues.secondary_bank_account_type;
    payload.secondary_bank_routing_number =
      formValues.secondary_bank_routing_number;
    payload.secondary_bank_account_number =
      formValues.secondary_bank_account_number;
    payload.secondary_bank_account_purpose =
      formValues.secondary_bank_account_purpose;
  } else {
    // else if the 'shouldShowSecondaryBankAccountFields' brand flag is false
    // then clear the secondary bank account information from the payload
    payload.secondary_bank_account_owner_name = '';
    payload.secondary_bank_account_type = '';
    payload.secondary_bank_routing_number = '';
    payload.secondary_bank_account_number = '';
    payload.secondary_bank_account_purpose = null;
  }

  if (payload.phone_number) {
    payload.phone_number = payload.phone_number
      .replace(/[\D]/g, '')
      .slice(0, 10);
  }

  return payload;
}

export function mapRegistrationToInitialFormValues(
  registration: Registration | null | undefined,
): FinancialFieldsValues {
  if (isEmpty(registration)) return {} as FinancialFieldsValues;

  // check if any secondary bank account fields are filled out
  const hasSecondaryAccount = hasSecondaryManualBankAccount(registration);

  const has0PercentOwnership =
    // using parseFloat here so we don't treat '' as 0
    !Number.isNaN(
      parseFloat(String(registration.meta?.ownership_percentage)),
      // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
    ) && +registration.meta?.ownership_percentage! === 0;

  const meta =
    !registration.meta || Array.isArray(registration.meta)
      ? {}
      : registration.meta;

  return {
    title: registration.title!,
    user_dob: registration.user_dob,
    user_ssn: registration.user_ssn,
    first_name: registration.first_name!,
    last_name: registration.last_name!,
    email: registration.email!,
    phone_number: registration.phone_number,
    owner_address_1: registration.owner_address_1,
    owner_address_2: registration.owner_address_2,
    owner_address_city: registration.owner_address_city,
    owner_address_state: registration.owner_address_state,
    owner_address_country: registration.owner_address_country ?? 'USA',
    owner_address_zip: registration.owner_address_zip,
    drivers_license_id: registration.drivers_license_id!,
    drivers_license_state: registration.drivers_license_state!,
    meta: {
      ...meta,
      ownership_percentage: has0PercentOwnership
        ? '0'
        : meta.ownership_percentage || '100',
      owner_citizenship: registration.meta?.owner_citizenship ?? 'USA',
      representatives: meta.representatives || [],
    },
    bank_account_owner_name:
      registration.bank_account_owner_name ||
      formatFirstAndLastNameToFullName(
        registration.first_name,
        registration.last_name,
      ),
    bank_account_type: registration.bank_account_type,
    bank_routing_number: registration.bank_routing_number,
    bank_account_number: registration.bank_account_number,
    secondary_bank_account_owner_name:
      registration.secondary_bank_account_owner_name ||
      formatFirstAndLastNameToFullName(
        registration.first_name,
        registration.last_name,
      ),
    secondary_bank_account_type: registration.secondary_bank_account_type,
    secondary_bank_routing_number: registration.secondary_bank_routing_number,
    secondary_bank_account_number: registration.secondary_bank_account_number,
    secondary_bank_account_purpose: registration.secondary_bank_account_purpose,
    // synthetic fields, not part of the state sent/retrieved from server/DB
    hasSecondaryAccount: !!hasSecondaryAccount,
    has0PercentOwnership: has0PercentOwnership,
    showManualAccountFields: false,
    bankAccountNumberConfirm: registration.bank_account_number,
    secondaryBankAccountNumberConfirm:
      registration.secondary_bank_account_number,
  } as FinancialFieldsValues;
}

export function mapFormValuesToTeamPayload(
  formValues: FinancialFieldsValues,
): Partial<Merchant> {
  return {
    // prettier-ignore
    contact_name: formatFirstAndLastNameToFullName(formValues.first_name, formValues.last_name),
  };
}

export function validateRequiredFormValues(
  values: FinancialFieldsValues,
  brandName?: string,
  brand?: Brand,
): FinancialFieldsErrors {
  const errors: FinancialFieldsErrors = { meta: {} };

  const requiredAdditionalRepresentativeFields: (keyof Representative)[] = [
    'type',
    'title',
    'first_name',
    'last_name',
    'ownership_percentage',
    'email',
    'date_of_birth',
    'ssn',
    'phone',
    'address_1',
    'address_city',
    // 'address_state',
    'address_zip',
    'address_country',
  ];

  if (brandName === 'fattmerchant' && brand?.showIdentificationDetails) {
    requiredAdditionalRepresentativeFields.push('drivers_license_id');
    requiredAdditionalRepresentativeFields.push('drivers_license_state');
  }

  const representatives = values.meta?.representatives || [];

  representatives.forEach((representative, i) => {
    let requiredFields = requiredAdditionalRepresentativeFields.slice(0);

    // For each representative, add state to the required field list if their country is USA
    if (representatives[i].address_country === 'USA') {
      requiredFields.push('address_state');
    }

    requiredFields.forEach(requiredField => {
      if (!representative[requiredField]) {
        const fieldPath = `meta.representatives.${i}.${requiredField}`;
        set(errors, fieldPath, 'Required');
      }
    });
  });

  const requiredOtherFields = [
    'title',
    'first_name',
    'last_name',
    // 'meta.ownership_percentage',
    'email',
    'user_dob',
    'user_ssn',
    'phone_number',
    'owner_address_1',
    'owner_address_city',
    // 'owner_address_state',
    'owner_address_zip',
    'owner_address_country',

    'bank_account_owner_name',
    'bank_routing_number',
    'bank_account_number',
  ];

  if (brandName === 'fattmerchant' && brand?.showIdentificationDetails) {
    requiredOtherFields.push('drivers_license_id');
    requiredOtherFields.push('drivers_license_state');
  }

  // If main signer selects US country, add state field requirement
  if (values.address_country === 'USA') {
    requiredOtherFields.push('address_state');
  }

  requiredOtherFields.forEach(field => {
    if (!get(values, field)) {
      const errorMessage = [
        'bank_account_number',
        'bank_routing_number',
        'bank_account_owner_name',
      ].includes(field)
        ? `${formatStringToSentence(field, '_')} is Required`
        : 'Required';
      set(errors, field, errorMessage);
    }
  });

  return errors;
}

export function validateFormValues(
  values: FinancialFieldsValues,
): FinancialFieldsErrors {
  const errors: FinancialFieldsErrors = { meta: {} };
  const representatives = values.meta?.representatives || [];

  const totalOwnershipPercentage = representatives.reduce(
    (sum, next) => sum + toNumber(String(next.ownership_percentage)),
    toNumber(String(values.ownership_percentage)),
  );

  const validateOwnershipPercentage = (
    ownershipPercentage: string | number | null | undefined,
  ) => {
    if (ownershipPercentage) {
      if (isNaN(+ownershipPercentage)) {
        return 'Must be a number';
      } else if (+ownershipPercentage > 100) {
        return 'Must not be over 100%';
      } else if (totalOwnershipPercentage > 100) {
        return 'Total % must not be over 100%';
      }
    }
  };

  representatives.forEach((representative, i) => {
    const representativeErrors: RepresentativeErrors = {};
    const ownershipPercentageError = validateOwnershipPercentage(
      representative.ownership_percentage,
    );

    if (ownershipPercentageError) {
      representativeErrors.ownership_percentage = ownershipPercentageError;
    }

    if (
      representative.ssn &&
      !isValidSSN(representative.ssn, representative.address_country)
    ) {
      representativeErrors.ssn =
        'Please enter a valid 9 digit Social Security Number';
    }

    if (
      (representative.title || representative.first_name) &&
      (!representative.citizenship ||
        !getCountry(representative.citizenship)?.iso)
    ) {
      representativeErrors.citizenship =
        'A valid country of citizenship must be provided for all representatives';
    }

    if (representative.phone && !isValidPhoneNumber(representative.phone)) {
      representativeErrors.phone = 'Please enter a valid phone number';
    }

    if (representative.email && !isValidEmail(representative.email)) {
      representativeErrors.email = 'Please use a valid email';
    }

    if (representative.address_1 && isAddressPoBox(representative.address_1)) {
      representativeErrors.address_1 = 'Address cannot be a PO Box';
    }

    if (representative.address_1 && isAddressApo(representative.address_1)) {
      representativeErrors.address_1 = 'Address cannot be an APO/FPO/DPO';
    }

    if (representative.address_2 && isAddressPoBox(representative.address_2)) {
      representativeErrors.address_2 = 'Address cannot be a PO Box';
    }

    if (representative.address_2 && isAddressApo(representative.address_2)) {
      representativeErrors.address_2 = 'Address cannot be an APO/FPO/DPO';
    }

    const representativeErrorsKeys = Object.keys(representativeErrors);

    if (representativeErrorsKeys.length) {
      if (!errors.meta) errors.meta = {};
      if (!errors.meta.representatives) errors.meta.representatives = [];
      if (!errors.meta.representatives[i]) errors.meta.representatives[i] = {};

      representativeErrorsKeys.forEach(key => {
        errors.meta!.representatives[i][key] = representativeErrors[key];
      });
    }
  });

  const ownershipPercentageError = validateOwnershipPercentage(
    values?.meta?.ownership_percentage,
  );

  if (ownershipPercentageError) {
    errors.meta!.ownership_percentage = ownershipPercentageError;
  } else if (
    !isNaN(+values.ownership_percentage) &&
    +values.ownership_percentage < 25 &&
    !values.has0PercentOwnership
  ) {
    errors.meta!.ownership_percentage = 'Must be greater than 25%';
  }

  if (values.phone_number && !isValidPhoneNumber(values.phone_number)) {
    errors.phone_number = 'Please enter a valid phone number';
  }

  if (values.email && !isValidEmail(values.email)) {
    errors.email = 'Please use a valid email';
  }

  if (values.owner_address_1 && isAddressPoBox(values.owner_address_1)) {
    errors.owner_address_1 = 'Address cannot be a PO Box';
  }

  if (values.owner_address_1 && isAddressApo(values.owner_address_1)) {
    errors.owner_address_1 = 'Address cannot be an APO/FPO/DPO';
  }

  if (values.owner_address_2 && isAddressPoBox(values.owner_address_2)) {
    errors.owner_address_2 = 'Address cannot be a PO Box';
  }

  if (values.owner_address_2 && isAddressApo(values.owner_address_2)) {
    errors.owner_address_2 = 'Address cannot be an APO/FPO/DPO';
  }

  // Check if confirmation field is populated as well so we don't display error
  // before it has been filled
  if (
    values.bank_account_number &&
    values.bankAccountNumberConfirm &&
    values.bank_account_number !== values.bankAccountNumberConfirm
  ) {
    errors.bankAccountNumberConfirm = 'Account Numbers Must Match';
  }

  if (
    values.secondary_bank_account_number &&
    values.secondaryBankAccountNumberConfirm &&
    values.secondary_bank_account_number !==
      values.secondaryBankAccountNumberConfirm
  ) {
    errors.secondaryBankAccountNumberConfirm = 'Account Numbers Must Match';
  }

  if (
    values.user_ssn &&
    !isValidSSN(values.user_ssn, values.owner_address_country)
  ) {
    errors.user_ssn = 'Please enter a valid 9 digit Social Security Number';
  }

  if (values?.bank_routing_number && values.bank_routing_number?.length !== 9) {
    errors.bank_routing_number = 'Routing number must be 9 characters';
  }

  if (
    values?.bank_account_number &&
    (values.bank_account_number?.length < 5 ||
      values.bank_account_number?.length > 17)
  ) {
    errors.bank_account_number = 'Please enter a valid bank account number';
  }

  if (
    values?.bank_account_owner_name &&
    values.bank_account_owner_name?.length < 2
  ) {
    errors.bank_account_owner_name =
      'Please enter a valid bank account owner name';
  }

  if (
    values?.secondary_bank_routing_number &&
    (values?.secondary_bank_routing_number?.length ?? 0) !== 9
  ) {
    errors.secondary_bank_routing_number =
      'Secondary routing number must be 9 characters';
  }

  if (
    values?.secondary_bank_account_number &&
    (values.secondary_bank_account_number?.length < 6 ||
      values.secondary_bank_account_number?.length > 17)
  ) {
    errors.secondary_bank_account_number =
      'Please enter a valid bank account number';
  }

  if (
    values?.secondary_bank_account_owner_name &&
    values?.secondary_bank_account_owner_name?.length < 2
  ) {
    errors.secondary_bank_account_owner_name =
      'Please enter a valid bank account owner name';
  }

  if (values.phone && !isValidPhoneNumber(values.phone)) {
    errors.phone = 'Please enter a valid phone number';
  }

  if (values.first_name && !isMeaningfulString(values.first_name)) {
    errors.first_name = 'Please enter a valid first name';
  }

  if (values.last_name && !isMeaningfulString(values.last_name)) {
    errors.last_name = 'Please enter a valid last name';
  }
  return errors;
}

export const formMutators: { [key: string]: Mutator<FinancialFieldsValues> } = {
  setFieldValue: ([name, value], state, utils) => {
    utils.changeValue(state, name, _ => value);
  },
  setCalculatedSignerOwnershipPercentage: (_, state, utils) => {
    utils.changeValue(state, 'meta.ownership_percentage', _ => {
      const values = state.formState.values as FinancialFieldsValues;
      const representatives = values.meta.representatives || [];

      const signerOwnershipPercentage = representatives.reduce(
        (sum, next) => sum - toNumber(String(next.ownership_percentage)),
        100,
      );

      return signerOwnershipPercentage;
    });
  },
  addAdditionalRepresentative: (_, state, utils) => {
    utils.changeValue(state, 'meta.representatives', representatives => [
      ...representatives,
      {
        title: '',
        date_of_birth: '',
        ssn: '',
        citizenship: 'USA',
        first_name: '',
        last_name: '',
        type: '',
        ownership_percentage: '',
        email: '',
        phone: '',
        address_1: '',
        address_2: '',
        address_city: '',
        address_state: '',
        address_country: '',
        address_zip: '',
      },
    ]);
  },
  removeAdditionalRepresentative: ([index], state, utils) => {
    utils.changeValue(state, 'meta.representatives', representatives => {
      const newRepresentatives = [...representatives];
      newRepresentatives.splice(index, 1);
      return newRepresentatives;
    });
  },
};

// this allows us to reuse the RepresentativeFields for both the signer (stored on the root level of registration) and any additional representatives (stored in meta.representatives)
export function getRepresentativeFieldNames(
  isSigner: boolean,
  index?: number,
): { [key: string]: RegistrationFieldPermissionsField } {
  return isSigner
    ? {
        title: 'title',
        date_of_birth: 'user_dob',
        ssn: 'user_ssn',
        first_name: 'first_name',
        last_name: 'last_name',
        type: null,
        ownership_percentage: 'meta.ownership_percentage',
        email: 'email',
        phone: 'phone_number',
        address_1: 'owner_address_1',
        address_2: 'owner_address_2',
        address_city: 'owner_address_city',
        address_state: 'owner_address_state',
        address_country: 'owner_address_country',
        address_zip: 'owner_address_zip',
        drivers_license_id: 'drivers_license_id',
        drivers_license_state: 'drivers_license_state',
        citizenship: 'meta.owner_citizenship',
      }
    : ({
        title: `meta.representatives.${index}.title`,
        date_of_birth: `meta.representatives.${index}.date_of_birth`,
        citizenship: `meta.representatives.${index}.citizenship`,
        ssn: `meta.representatives.${index}.ssn`,
        first_name: `meta.representatives.${index}.first_name`,
        last_name: `meta.representatives.${index}.last_name`,
        type: `meta.representatives.${index}.type`,
        ownership_percentage: `meta.representatives.${index}.ownership_percentage`,
        email: `meta.representatives.${index}.email`,
        phone: `meta.representatives.${index}.phone`,
        address_1: `meta.representatives.${index}.address_1`,
        address_2: `meta.representatives.${index}.address_2`,
        address_city: `meta.representatives.${index}.address_city`,
        address_state: `meta.representatives.${index}.address_state`,
        address_country: `meta.representatives.${index}.address_country`,
        address_zip: `meta.representatives.${index}.address_zip`,
        drivers_license_id: `meta.representatives.${index}.drivers_license_id`,
        drivers_license_state: `meta.representatives.${index}.drivers_license_state`,
      } as unknown as any);
}

export const necessaryFinancialFields: RegistrationFieldPermissionsField[] = [
  // primary bank fields
  'bank_account_owner_name',
  'bank_routing_number',
  'bank_account_number',

  // NOTE secondary_bank_* (secondary bank account) fields are not necessary

  // business owner fields
  'title',
  'user_dob',
  'user_ssn',
  'first_name',
  'last_name',
  'meta.ownership_percentage',
  'email',
  'phone_number',
  'owner_address_1',
  // NOTE: 'owner_address_2' field is not necessary
  'owner_address_city',
  // NOTE: 'owner_address_state' field is not necessary (unless 'owner_address_country' === 'USA' (handled below))
  'owner_address_country',
  'owner_address_zip',
];

export function areFinancialFieldsHidden(
  brand: Brand | null,
  registration: Registration | null,
) {
  const necessaryFields = [...necessaryFinancialFields];

  if (registration?.owner_address_country === 'USA') {
    // necessitate 'owner_address_state' field if owner_address_country is USA
    necessaryFields.push('owner_address_state');
  }

  if (!registration?.meta?.representatives?.length) {
    // if there are no additional representatives, necessitate additional representative fields
    necessaryFields.push(
      'meta.representatives.0.title',
      'meta.representatives.0.date_of_birth',
      'meta.representatives.0.first_name',
      'meta.representatives.0.last_name',
      'meta.representatives.0.type',
      'meta.representatives.0.citizenship',
      'meta.representatives.0.ssn',
      'meta.representatives.0.ownership_percentage',
      'meta.representatives.0.email',
      'meta.representatives.0.phone',
      'meta.representatives.0.address_1',
      // NOTE: 'meta.representatives.0.address_2' field is not necessary
      'meta.representatives.0.address_city',
      // NOTE: have to show address_state since we don't have an address_country (since there are no reps, as assured by this if statement)
      'meta.representatives.0.address_state',
      'meta.representatives.0.address_country',
      'meta.representatives.0.address_zip',
    );
  }

  const { none } = getCombinedFieldsVisibilityLevels(
    registration,
    brand,
    ...necessaryFields,
  );

  /**
   * IF none of the necessary fields are visible (i.e. they have been prefilled or otherwise turned off)
   * THEN return true (do not show this page)
   */
  return none;
}

export function isFinancialPageHidden(brand: Brand) {
  return !brand.regSidebarItems?.includes('Signer/Financial');
}
